// Custody Calendar Configuration
import { RRule } from 'rrule';
import type { CustodyScheme, Weekday } from '../types/custody';

// Available custody schemes
export const CUSTODY_SCHEMES: Array<{ key: CustodyScheme; label: string; description: string }> = [
  { key: 'day', label: 'Every Other Day', description: 'Custody alternates daily' },
  { key: 'xdays', label: 'Every X Days', description: 'Custody changes every specified number of days' },
  { key: 'week', label: 'Every Other Week', description: 'Custody alternates weekly' },
  { key: 'xweeks', label: 'Every X Weeks', description: 'Custody changes every specified number of weeks' },
  { key: 'weekends', label: 'Every Other Weekend', description: 'Weekend custody alternates' },
  { key: 'xweekends', label: 'Every X Weekends', description: 'Weekend custody every specified number of weekends' },
  { key: 'weekend_splitweek', label: 'Every Other Weekend + Split Week', description: 'Alternating weekends with split weekdays' },
  { key: 'weekend_weekdays', label: 'Weekend / Weekdays', description: 'One household has weekends, other has weekdays' },
  { key: 'alternating', label: 'Alternating Splits', description: 'Custom alternating pattern with day-by-day control' },
  { key: '223', label: '2-2-3 Schedule', description: 'Two days, two days, three days — equal time over 2-week cycle' },
];

// Weekday options
export const WEEKDAYS: Array<{ key: Weekday; label: string; short: string }> = [
  { key: 'Sunday', label: 'Sunday', short: 'SU' },
  { key: 'Monday', label: 'Monday', short: 'MO' },
  { key: 'Tuesday', label: 'Tuesday', short: 'TU' },
  { key: 'Wednesday', label: 'Wednesday', short: 'WE' },
  { key: 'Thursday', label: 'Thursday', short: 'TH' },
  { key: 'Friday', label: 'Friday', short: 'FR' },
  { key: 'Saturday', label: 'Saturday', short: 'SA' },
];

// Maps weekday names to numeric values (0 = Sunday)
export const WEEKDAY_MAP: Record<Weekday, number> = {
  'Sunday': 0,
  'Monday': 1,
  'Tuesday': 2,
  'Wednesday': 3,
  'Thursday': 4,
  'Friday': 5,
  'Saturday': 6,
};

// Maps weekday names to RRule weekday constants
export const RRULE_WEEKDAY_MAP: Record<Weekday, typeof RRule.SU> = {
  'Sunday': RRule.SU,
  'Monday': RRule.MO,
  'Tuesday': RRule.TU,
  'Wednesday': RRule.WE,
  'Thursday': RRule.TH,
  'Friday': RRule.FR,
  'Saturday': RRule.SA,
};

// Short day codes to weekday names
export const SHORT_TO_WEEKDAY: Record<string, Weekday> = {
  'SU': 'Sunday',
  'MO': 'Monday',
  'TU': 'Tuesday',
  'WE': 'Wednesday',
  'TH': 'Thursday',
  'FR': 'Friday',
  'SA': 'Saturday',
};

// Day of month options (1-31)
export const MONTH_DAYS = Array.from({ length: 31 }, (_, i) => ({
  key: i + 1,
  label: `${i + 1}`,
}));

// Week count options (1-52)
export const WEEKS = Array.from({ length: 52 }, (_, i) => ({
  key: i + 1,
  label: `${i + 1}`,
}));

// Month count options (1-12)
export const MONTHS = Array.from({ length: 12 }, (_, i) => ({
  key: i + 1,
  label: `${i + 1}`,
}));

// Day count options (1-365)
export const DAYS = Array.from({ length: 365 }, (_, i) => ({
  key: i + 1,
  label: `${i + 1}`,
}));

// Default custody colors for households
export const DEFAULT_HOUSEHOLD_COLORS = [
  '#4A90D9', // Blue
  '#E57373', // Red/Pink
  '#81C784', // Green
  '#FFB74D', // Orange
  '#9575CD', // Purple
  '#4DB6AC', // Teal
];

// Helper function to get ordinal suffix
export const getOrdinalSuffix = (n: number): string => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

// Format date for display
export const formatCustodyDate = (date: Date | number): string => {
  const d = typeof date === 'number' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Convert date to YYYY-MM-DD string
export const dateToString = (date: Date | number): string => {
  const d = typeof date === 'number' ? new Date(date) : date;
  return d.toISOString().split('T')[0];
};
